import React, { useEffect, useId, useRef, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import {
  US_STATES,
  formatStateAbbr,
  normalizeStateValue,
  stateByPrefix,
} from "../constants/usStates";

const TYPEAHEAD_MS = 700;

export default function StateSelect({
  value,
  onChange,
  label,
  name = "state",
  placeholder = "Select a state",
  fullWidth = true,
  size,
  required = false,
  disabled = false,
  sx,
}) {
  const labelId = useId();
  const stored = normalizeStateValue(value);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(stored);
  const bufferRef = useRef("");
  const lastKeyAtRef = useRef(0);
  const jumpedRef = useRef(false);
  const itemRefs = useRef({});

  const extraOption =
    stored && !US_STATES.some((state) => state.abbr === stored) ? stored : "";

  useEffect(() => {
    if (!open || !highlight) return undefined;
    const frame = requestAnimationFrame(() => {
      const node = itemRefs.current[highlight];
      if (node?.scrollIntoView) node.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, highlight]);

  const commit = (abbr) => {
    onChange?.({ target: { name, value: abbr } });
    setHighlight(abbr);
    setOpen(false);
    bufferRef.current = "";
  };

  const jumpToPrefix = (key) => {
    const now = Date.now();
    const elapsed = now - lastKeyAtRef.current;
    lastKeyAtRef.current = now;
    const letter = key.toLowerCase();
    const prev = elapsed > TYPEAHEAD_MS ? "" : bufferRef.current;
    const repeatLetter = prev.length === 1 && prev === letter;
    const query = repeatLetter ? letter : `${prev}${letter}`;
    const after = repeatLetter ? highlight || stored || null : null;
    const prefixed = stateByPrefix(query, after);
    const match = prefixed || stateByPrefix(letter);
    if (!match) return;
    bufferRef.current = prefixed ? query : letter;
    jumpedRef.current = true;
    setHighlight(match.abbr);
    setOpen(true);
  };

  const moveHighlight = (direction) => {
    const current = US_STATES.findIndex((state) => state.abbr === highlight);
    if (direction > 0) {
      const next = US_STATES[current < 0 ? 0 : Math.min(US_STATES.length - 1, current + 1)];
      setHighlight(next.abbr);
      return;
    }
    const prev = US_STATES[current < 0 ? 0 : Math.max(0, current - 1)];
    setHighlight(prev.abbr);
  };

  const handleKeyDown = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const key = event.key;

    if (/^[a-z]$/i.test(key)) {
      event.preventDefault();
      event.stopPropagation();
      jumpToPrefix(key);
      return;
    }

    if (!open) return;

    if (key === "ArrowDown" || key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      moveHighlight(key === "ArrowDown" ? 1 : -1);
      return;
    }

    if (key === "Home") {
      event.preventDefault();
      event.stopPropagation();
      setHighlight(US_STATES[0].abbr);
      return;
    }

    if (key === "End") {
      event.preventDefault();
      event.stopPropagation();
      setHighlight(US_STATES[US_STATES.length - 1].abbr);
      return;
    }

    if ((key === "Enter" || key === " ") && highlight) {
      event.preventDefault();
      event.stopPropagation();
      commit(highlight);
    }
  };

  const handleDisplayKeyDown = (event) => {
    handleKeyDown(event);
    if (event.defaultPrevented || disabled) return;
    if (!open && [" ", "ArrowUp", "ArrowDown", "Enter"].includes(event.key)) {
      event.preventDefault();
      setHighlight(stored || "");
      setOpen(true);
    }
  };

  return (
    <FormControl fullWidth={fullWidth} size={size} required={required} disabled={disabled} sx={sx}>
      {label ? <InputLabel id={labelId}>{label}</InputLabel> : null}
      <Select
        labelId={label ? labelId : undefined}
        label={label || undefined}
        name={name}
        value={stored}
        displayEmpty
        open={open}
        onOpen={() => {
          setOpen(true);
          if (jumpedRef.current) {
            jumpedRef.current = false;
            return;
          }
          setHighlight(stored || "");
        }}
        onClose={() => {
          setOpen(false);
          bufferRef.current = "";
        }}
        onChange={(event) => commit(event.target.value)}
        SelectDisplayProps={{ onKeyDown: handleDisplayKeyDown }}
        renderValue={(selected) => {
          const abbr = formatStateAbbr(selected);
          if (abbr) return abbr;
          if (label) return <span />;
          return (
            <Typography component="span" color="text.secondary" sx={{ fontSize: "inherit" }}>
              {placeholder}
            </Typography>
          );
        }}
        MenuProps={{
          autoFocus: false,
          disableAutoFocusItem: true,
          slotProps: {
            paper: {
              sx: {
                borderRadius: "12px",
                mt: 0.75,
                maxHeight: 360,
                boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
              },
            },
          },
        }}
        sx={{
          borderRadius: 2.5,
          "& .MuiSelect-select": { py: size === "small" ? 1 : 1.35 },
        }}
      >
        <MenuItem value="">
          <Typography color="text.secondary">{placeholder}</Typography>
        </MenuItem>
        {extraOption ? <MenuItem value={extraOption}>{extraOption}</MenuItem> : null}
        {US_STATES.map((state) => (
          <MenuItem
            key={state.abbr}
            value={state.abbr}
            ref={(node) => {
              itemRefs.current[state.abbr] = node;
            }}
            sx={{
              borderRadius: 1.5,
              mx: 0.75,
              my: 0.15,
              ...(highlight === state.abbr && { bgcolor: "action.selected" }),
            }}
          >
            {state.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
